import React from "react";
import { Box, Button, Typography } from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import heroBackground from "../../assets/images/top_background.png";
import heroLeftDecor from "../../assets/images/qrc_half_cir.png";
import heroIllustration from "../../assets/images/top_object.png";

// Figma exports, at their natural sizes:
//   top_background.png  1068 x 250 — world-map wash, teal circles, swirl lines (full band)
//   qrc_half_cir.png     848 x 250 — blue dot grid + yellow half circle, anchored left
//   top_object.png       293 x 181 — support agent illustration
const HERO_HEIGHT = { xs: 175, sm: 250 };
// The design renders the 293x181 export at ~232x143 and lifts it so its base clears the top
// edge of the card that overlaps the band (card top = 250 - 92 = 158px from the hero top).
// At full size the illustration reached 209px and its lower quarter disappeared behind the card.
const ILLUSTRATION = { width: 232, height: 163, offsetTop: "-14px" };

interface PreLoginHeroProps {
  // "greeting" is the Help Center landing headline; "request" is used once the visitor is
  // inside the verify / raise-a-request flow.
  variant?: "greeting" | "request";
  // Pass to show the Logout pill in the band's top-right corner. Omit before the visitor has
  // a verified QRC session — there is nothing to log out of yet.
  onLogout?: () => void;
}

// Hero band shared by the pre-login Help Center and the QRC Contact Us hub: headline + intro
// copy on the left, support illustration on the right, over the two background layers. The
// card that follows is meant to overlap its lower portion via a negative top margin.
const PreLoginHero: React.FC<PreLoginHeroProps> = ({ variant = "greeting", onLogout }) => {
  const titleLines =
    variant === "request" ? ["Raising a", "Service Request"] : ["Hi there, \u{1F44B}", "How can we help you today?"];

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        height: HERO_HEIGHT,
        // Left decor sits above the map band; both keep their 250px design height.
        backgroundImage: `url(${heroLeftDecor}), url(${heroBackground})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "left top, right top",
        backgroundSize: "auto 100%, cover",
        backgroundColor: "#F7FBFB",
      }}
    >
      {/* Logout — top-right of the band, above the illustration and clear of the background's
          teal dots. This is the only session control on the pre-login screens now that the
          "Customer Support" welcome bar is gone. */}
      {onLogout && (
        <Button
          onClick={onLogout}
          startIcon={<LogoutOutlinedIcon sx={{ fontSize: 15 }} />}
          sx={{
            position: "absolute",
            top: 12,
            right: { xs: 12, sm: 20 },
            zIndex: 2,
            bgcolor: "#fff",
            border: "1.5px solid #00B3B5",
            borderRadius: "999px",
            px: 1.5,
            py: 0.3,
            minWidth: 0,
            color: "#1a1a1a",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            "& .MuiButton-startIcon": { mr: 0.5, ml: 0, color: "#00B3B5" },
            "&:hover": { bgcolor: "#fff", borderColor: "#009799" },
          }}
        >
          Logout
        </Button>
      )}

      <Box
        sx={{
          height: "100%",
          // Copy is inset to line up with the content of the card below (page gutter + card
          // padding); the extra right padding keeps the illustration clear of the background's
          // dotted/swirl corner, as in the design.
          pl: { xs: 2, sm: 5 },
          pr: { xs: 2, sm: 16 },
          pt: { xs: 2.5, sm: 3.5 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 3,
        }}
      >
        <Box>
          {titleLines.map((line) => (
            <Typography
              key={line}
              sx={{ fontWeight: 700, fontSize: { xs: "20px", sm: "26px" }, color: "#1a1a1a", lineHeight: 1.3 }}
            >
              {line}
            </Typography>
          ))}
          <Typography
            sx={{
              color: "#6b7280",
              fontSize: { xs: "12px", sm: "13px" },
              mt: { xs: 1, sm: 1.5 },
              maxWidth: 470,
              lineHeight: 1.6,
            }}
          >
            Get instant guidance, track your requests, or access tools to manage your loan&mdash;quickly and
            securely.
          </Typography>
        </Box>

        <Box
          component="img"
          src={heroIllustration}
          alt="Avanse customer support"
          sx={{
            display: { xs: "none", sm: "block" },
            width: ILLUSTRATION.width,
            height: ILLUSTRATION.height,
            mt: ILLUSTRATION.offsetTop,
            flexShrink: 0,
          }}
        />
      </Box>
    </Box>
  );
};

export default PreLoginHero;
